import { CatalogMapping, SearchQuery } from 'modules/domains';
import { deleteRaw, getRaw, postRaw, putRaw, get } from 'services/api';

export const CATALOG_URL = '/i/catalogs';
export const CATALOG_COLLECTIONS_URL = '/i/catalogCollections';
export const FIELD_MAPPINGS = '/settings/project/getFieldMappings';
export const CATALOG_PREVIEW = '/i/catalogs/previewQuery';
export const GET_CATALOG_FIELD_FACETS = '/i/catalogs/:catalogName/fieldFacets/:fieldName';
export const CATALOG_FIELD_FACETS_SUBPATH = 'fieldFacets';

export const getCatalogFieldMappingsUrl = (catalogName: string) =>
  `${CATALOG_URL}/${encodeURIComponent(catalogName)}/fieldMappings`;

export const getCatalogFieldFacetsUrl = (catalogName: string, fieldName: string) =>
  `${CATALOG_URL}/${encodeURIComponent(
    catalogName,
  )}/${CATALOG_FIELD_FACETS_SUBPATH}/${encodeURIComponent(fieldName)}`;

export function getCatalogs() {
  return getRaw(CATALOG_URL);
}

// this will get paginated values
export function getCatalogPaginatedItems({
  tableName,
  page,
  pageSize,
  orderBy,
  sortAscending,
}: {
  tableName: string;
  page: number;
  pageSize: number;
  orderBy: string;
  sortAscending: boolean;
}) {
  // optional orderBy, sortAscending only works for typed fields and not 'key' or 'lastModified'
  switch (true) {
    case orderBy === 'key' || orderBy === 'lastModified':
      return getRaw(`${CATALOG_URL}/${tableName}?page=${page}&pageSize=${pageSize}`);
    case orderBy && orderBy !== 'key' && orderBy !== 'lastModified':
      return getRaw(
        `${CATALOG_URL}/${tableName}?page=${page}&pageSize=${pageSize}&orderBy=${orderBy}&sortAscending=${sortAscending}`,
      );
    default:
      return getRaw(`${CATALOG_URL}/${tableName}?page=${page}&pageSize=${pageSize}`);
  }
}

export function deleteCatalog(catalogName: string) {
  return deleteRaw(`${CATALOG_URL}/${catalogName}`);
}

export function deleteCatalogItem(catalogName: string, itemId: string) {
  return deleteRaw(`${CATALOG_URL}/${catalogName}/items/${itemId}`);
}

// TODO catalogName should just be in params
export function createNewCatalog(catalogName: string) {
  return postRaw(`${CATALOG_URL}/new?catalogName=${catalogName}`, { catalogName });
}

// check with bfd team for different endpoints for create and update?
// TODO catalogName and itemId need to be in params
export function createOrUpdateCatalogItem<P>(catalogName: string, itemId: string, params: P) {
  return putRaw(`${CATALOG_URL}/update?catalogName=${catalogName}&itemId=${itemId}`, params);
}

const multiPartFormHeader = {
  headers: {
    'Content-Type': 'multipart/form-data',
  },
};

// TODO do not use query string param here, tableName should be in the form
export function bulkUpdateCatalogItems<P>(catalogName: string, params: P) {
  return postRaw(`${CATALOG_URL}/bulkUpdate?tableName=${catalogName}`, params, multiPartFormHeader);
}

export function getCatalogFieldMappings(catalogName: string) {
  return getRaw(`${CATALOG_URL}/${catalogName}/fieldMappings`);
}

export function updateCatalogFieldMappings(catalogName: string, mappingsUpdates: CatalogMapping[]) {
  const params = {
    mappingsUpdates,
  };
  return putRaw(`${CATALOG_URL}/${catalogName}/fieldTypes`, params);
}

/*------------------------------------------------------------------------------------------------*/
/* Catalog Collections                                                                            */
/*------------------------------------------------------------------------------------------------*/
/**
 * getCatalogCollections
 * Fetches catalog collections.
 * @param { Object } params: Additional fetch params for the catalog collections.
 * @param { Object } response: The response to get back from the api.
 * @returns { Promise } The response from the api.
 */
export const getCatalogCollections = <P>(params: P) =>
  getRaw(`${CATALOG_COLLECTIONS_URL}`, { params });

/**
 * deleteCatalogCollection
 * Deletes a catalog collection.
 * @param { String } id: The id of the collection to delete.
 * @returns { Promise } The response from the api.
 */
export const deleteCatalogCollection = (id: string) =>
  deleteRaw(`${CATALOG_COLLECTIONS_URL}/${id}`);

/**
 * getCatalogCollection
 * Gets a catalog collection by id.
 * @param { String } id: Id of the catalog collection to get.
 * @returns { Promise } The response from the api with the data of the fetched catalog collection.
 */
export const getCatalogCollection = (id: string) => getRaw(`${CATALOG_COLLECTIONS_URL}/${id}`);

/**
 * createCatalogCollection
 * Creates a new catalog collection.
 * @param { Object } params: Parameters containing the searchQuery, name and other data for the catalog collection.
 * @returns { Promise } The response from the api from creating a catalog collection.
 */
export const createCatalogCollection = <P>(params: P) =>
  postRaw(`${CATALOG_COLLECTIONS_URL}`, params);

/**
 * previewCatalogCollection
 * Previews a catalog collection.
 * @param { String } collectionName Name of the catalog collection to preview.
 * @param { String } [emailOpt] User email to preview catalog collection with.
 * @param { String } [userIdOpt] User id to preview catalog collection with.
 * @returns { Promise } The response from the api from creating a catalog collection.
 */
export const previewCatalogCollection = (
  collectionName: string,
  emailOpt: string | null,
  userIdOpt: string | null,
) => {
  const params: { emailOpt?: string; userIdOpt?: string } = {};
  if (emailOpt) params.emailOpt = emailOpt;
  if (userIdOpt) params.userIdOpt = userIdOpt;

  return getRaw(`${CATALOG_COLLECTIONS_URL}/previewCollection/${collectionName}`, { params });
};

/**
 * updateCatalogCollection
 * Updates a new catalog collection.
 * @param { Number } id: The is of the catalog to update.
 * @param { Object } params: Parameters containing the searchQuery, name and other data of the catalog collection.
 * @returns { Promise } The response from the api from updating a catalog collection.
 */
export const updateCatalogCollection = <P>(id: string, params: P) =>
  postRaw(`${CATALOG_COLLECTIONS_URL}/${id}`, params);

/**
 * new api call for catalog collection builder. We want to use this instead of '/analytics/userMappings'
 */
export const getFieldMappings = () => get<CatalogMapping[]>(FIELD_MAPPINGS);

/**
 * new api call for getting catalog preview data based on catalogQuery
 * @param { String } [catalogName] Name of the catalog to preview.
 * @param { SearchQuery } [searchQuery] search query built from for the catalogQuery. Type from import { SearchQuery } from 'modules/domains';
 * @returns { Promise } The response from the api from catalog preview query.
 */

export const previewCatalogPreviewQuery = (catalogName?: string, searchQuery?: SearchQuery) =>
  postRaw(CATALOG_PREVIEW, { catalogName, searchQuery });

/**
 * new api call for fetching field facets for catalog/collection builder
 * @param {*} catalogName name of the catalog
 * @param {*} fieldName name of the field in catalog
 * @returns The typeahead for catalog field
 */

export const fetchCatalogFieldFacets = (catalogName = '', fieldName = '') => {
  return get(getCatalogFieldFacetsUrl(catalogName, fieldName));
};
